import React, { useCallback, useEffect, useState } from "react";

import BaseCard from "@busbud/design-system-components/dist/Card";
import BaseCardContent from "@busbud/design-system-components/dist/CardContent";
import CardHeader from "@busbud/design-system-components/dist/CardHeader";
import CircularProgress from "@busbud/design-system-components/dist/CircularProgress";
import LayoutSection from "@busbud/design-system-components/dist/Layout/LayoutSection";
import Stack from "@busbud/design-system-components/dist/Stack";
import { Select } from "@busbud/horizon";

import { BackButton } from "@app/components/back-button";
import { ExpandableCardContent } from "@app/components/card-with-header/expandable-card";
import { CLASSES_AND_FARES_CARD_NAME } from "@app/components/drawers/selection-options/selection-options.constants";
import { ExpandableCardsAccordion } from "@app/components/expandable-cards-accordion";
import { Itinerary } from "@app/components/itinerary";
import { useFetchSegmentStopsIfNotLoaded } from "@app/components/itinerary/use-fetch-trip-segment-stops";
import {
  NavigationWrapperLayout,
  patchSearchAdultWithSelectedSeats
} from "@app/components/navigation-elements/navigation-wrapper-layout";
import { useAppContext } from "@app/helpers/hooks";
import type { TripOption } from "@app/types/results-types";

import type { ConnectorProps } from "./ticket-options.connect";

import { ClassesList } from "./classes";
import { FaresList } from "./fares";
import { showPassengerSelector } from "./helpers";
import { useSegmentsIdWithSeatSelection } from "./hooks/use-segments-id-with-seat-selection";
import { OperatorRatingsCardHeader } from "./operator-ratings/operator-ratings-card-header.connect";
import { OperatorRatings } from "./operator-ratings/operator-ratings.connect";
import { PassengerSelector } from "./passenger-selector/passenger-selector";
import { usePassengerStore } from "./passenger-selector/passenger-store";
import { PriceLockSmokeTest } from "./price-lock-smoke-test";
import { useStyles } from "./ticket-options.styles";
import { UrgencyAlert } from "../results/components/inventory-alerts/urgency-alert/urgency-alert";
import { shouldRemoveNavigationJourneyId } from "../results/helpers/should-remove-navigation-search-params";
import { useNavigation } from "../results/hooks/use-navigation";
import { useFetchSegmentVehicleIfNotLoaded } from "../seat-selection-first/hooks/use-fetch-segment-vehicle";
import { useSeatSelectionFirst } from "../seat-selection-first/hooks/use-seat-selection-first";

const Card: React.FC<React.ComponentProps<typeof BaseCard>> = props => (
  <BaseCard borderRadius="xl" boxShadow="unset" {...props} />
);

const CardContent: React.FC<
  React.ComponentProps<typeof BaseCardContent>
> = props => {
  const { classes } = useStyles();

  return <BaseCardContent className={classes.cardContent} {...props} />;
};

export const TicketOptions: React.FC<ConnectorProps> = ({
  default_trip_id,
  default_trip_by_journey_id,
  error,
  is_amenities_selection_hidden,
  is_external,
  is_loading_vehicle,
  is_vehicle_available,
  operator_has_ratings,
  trip_options_matrix,
  result_search_count,
  inventory_ready,
  search,
  stops_loaded,
  unique_destinations,
  search_trip_id
}) => {
  const { classes } = useStyles();
  const { translator, features } = useAppContext();
  const {
    goBack,
    redirectToResults,
    selectTrip,
    leg_card_id,
    is_roundtrip,
    cart_id,
    journey_id,
    setJourneyId
  } = useNavigation();
  const { selected_seats_count } = usePassengerStore();
  const { is_seat_selection_first_enabled } = useSeatSelectionFirst();
  const [selected_trip_id, selectTripId] = useState<string | undefined>();

  // If the journey_id can't be matched, remove it from the URL
  useEffect(() => {
    if (
      shouldRemoveNavigationJourneyId(
        journey_id,
        default_trip_by_journey_id,
        inventory_ready
      )
    ) {
      setJourneyId(null);
    }
  }, [journey_id, default_trip_by_journey_id, inventory_ready, setJourneyId]);

  const active_trip_id: string | undefined =
    inventory_ready && trip_options_matrix.length
      ? selected_trip_id || search_trip_id || default_trip_id
      : undefined;

  const { segments_id_with_seat_selection } =
    useSegmentsIdWithSeatSelection(active_trip_id);

  useFetchSegmentVehicleIfNotLoaded(
    active_trip_id ? [active_trip_id] : [],
    !is_seat_selection_first_enabled || is_external
  );
  useFetchSegmentStopsIfNotLoaded(stops_loaded, default_trip_id);

  useEffect(() => {
    // without leg_card_id, there's an infinite loop by the time the component exists
    if (error && leg_card_id) {
      goBack(translator.t("!error.CartDepartureExpired.text"));
    }
  }, [error, goBack, leg_card_id, translator]);

  useEffect(() => {
    // If the inventory is ready and the trip cannot be found, redirect back to the results page
    if (inventory_ready && !active_trip_id) {
      redirectToResults(
        {
          error: translator.t("!error.CartDepartureExpired.text")
        },
        { force_refresh: true, restart_spa_search: true }
      );
    }
  }, [redirectToResults, inventory_ready, active_trip_id, translator]);

  const first_segment_id_with_seat_selection =
    segments_id_with_seat_selection[0]?.id;

  const onClickContinue = useCallback(
    (trip: TripOption) => {
      return selectTrip(
        trip,
        is_external,
        is_vehicle_available,
        selected_seats_count,
        first_segment_id_with_seat_selection
      );
    },
    [
      is_external,
      is_vehicle_available,
      selected_seats_count,
      selectTrip,
      first_segment_id_with_seat_selection
    ]
  );

  if (error) {
    return null;
  }

  const renderLoader = () => (
    <div className={classes.loader}>
      <CircularProgress />
    </div>
  );

  const show_passenger_selector = showPassengerSelector({
    is_external,
    is_seat_selection_first_enabled,
    is_vehicle_available,
    is_roundtrip,
    cart_id
  });

  const disabled = !!is_seat_selection_first_enabled && is_loading_vehicle;

  const search_with_selected_seats_count = is_seat_selection_first_enabled
    ? patchSearchAdultWithSelectedSeats(search, selected_seats_count)
    : search;

  const selected_trip = trip_options_matrix
    .flat()
    .find(m => m.trip_id === active_trip_id);

  return (
    <>
      <LayoutSection className={classes.pageHeader}>
        <BackButton goBack={goBack} />
      </LayoutSection>
      <NavigationWrapperLayout
        disabled={disabled}
        is_external={is_external}
        selected_trip={selected_trip}
        step_label={translator.t(
          "!results.classes-fares.review-ticket-options"
        )}
        trip_options_matrix={trip_options_matrix}
        onClickContinue={onClickContinue}
        search={search_with_selected_seats_count}
      >
        {!!result_search_count && (
          <LayoutSection yMargin={{ xs: 2, lg: 3 }}>
            <UrgencyAlert
              result_search_count={result_search_count}
              has_results={true}
              has_alternatives={true}
              error_type={null}
              inventory_ready={inventory_ready}
            />
          </LayoutSection>
        )}
        {!!show_passenger_selector && (
          <LayoutSection yMargin={{ xs: 2, lg: 3 }}>
            <PassengerSelector is_loading={is_loading_vehicle} />
          </LayoutSection>
        )}
        <LayoutSection>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, lg: 3, xl: 6 }}
            alignItems={{ sm: "flex-start" }}
          >
            <Stack
              direction="column"
              spacing={{ xs: 2, lg: 3 }}
              flex={{ xs: "1", md: "3" }}
              minWidth={0} // Forces the block to shrink when the element is full
            >
              <PriceLockSmokeTest
                price_in_cents={selected_trip?.price_in_cents}
              />
              {unique_destinations.length > 1 && (
                <Card>
                  <CardHeader
                    title={translator.t(
                      "!results.shuttle-card.select-destination"
                    )}
                  />
                  <CardContent>
                    <Select
                      labelText={translator.t(
                        "!search.input.destination.label"
                      )}
                      value={
                        // if there is no journey_id in the query param,
                        // select the journey_id of the farthest destination (longest trip) by default.
                        journey_id ??
                        unique_destinations[unique_destinations.length - 1]
                          ?.journey_id
                      }
                      onChange={e => {
                        selectTripId(undefined);
                        setJourneyId(e.target.value);
                      }}
                    >
                      {unique_destinations.map(
                        ({ journey_id, destination_name }) => (
                          <Select.Option key={journey_id} value={journey_id}>
                            {destination_name}
                          </Select.Option>
                        )
                      )}
                    </Select>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader
                  title={translator.t("!results.classes-fares.trip-selection")}
                />
                <CardContent>
                  {!active_trip_id ? (
                    renderLoader()
                  ) : (
                    <Itinerary
                      trip_id={active_trip_id}
                      renderLoader={renderLoader}
                    />
                  )}
                </CardContent>
              </Card>
              {!!operator_has_ratings && (
                <ExpandableCardsAccordion
                  boxShadow="unset"
                  borderRadius="xl"
                  hidden={!features.SHOW_REVIEWS_FOR_TRIP_DETAILS}
                >
                  <ExpandableCardContent
                    header={
                      <OperatorRatingsCardHeader trip_id={active_trip_id} />
                    }
                    is_expanded={!features.COLLAPSE_OPERATOR_REVIEWS}
                    show_divider={false}
                  >
                    <CardContent>
                      <OperatorRatings trip_id={active_trip_id} />
                    </CardContent>
                  </ExpandableCardContent>
                  {/* We use an empty fragment here because, ExpandableCardsAccordion expects multiple children. */}
                  <></>
                </ExpandableCardsAccordion>
              )}
            </Stack>
            <Stack
              direction="column"
              spacing={{ xs: 2, lg: 3 }}
              flex={{ xs: "1", md: "3" }}
              minWidth={0} // Forces the block to shrink when the element is full
            >
              {!is_amenities_selection_hidden && (
                <Card>
                  <CardHeader
                    title={translator.t(
                      "!results.classes-fares.comfort-options"
                    )}
                  />
                  <CardContent>
                    <ClassesList
                      leg_card_id={leg_card_id}
                      onSelect={selectTripId}
                      selected_trip_id={active_trip_id}
                      journey_id={journey_id}
                    />
                  </CardContent>
                </Card>
              )}
              <ExpandableCardsAccordion boxShadow="unset" borderRadius="xl">
                <ExpandableCardContent
                  key={CLASSES_AND_FARES_CARD_NAME}
                  is_expanded
                  header={translator.t(
                    "!results.classes-fares.flexibility-conditions"
                  )}
                  wrapper_classes={classes.cardContent}
                  show_divider={false}
                >
                  <CardContent style={{ paddingTop: 0 }}>
                    <FaresList
                      leg_card_id={leg_card_id}
                      selectTripId={selectTripId}
                      selected_trip_id={active_trip_id}
                      journey_id={journey_id}
                      loading={!active_trip_id}
                    />
                  </CardContent>
                </ExpandableCardContent>
              </ExpandableCardsAccordion>
            </Stack>
          </Stack>
        </LayoutSection>
      </NavigationWrapperLayout>
    </>
  );
};
